const headerRegex = /@@ -(\d+),(\d+) \+(\d+),(\d+) @@/;
export interface HeaderDiffLine {
  type: 'header';
  from: { line: number; count: number };
  to: { line: number; count: number };
}

const commonRegex = /^ (.*)/;
export interface CommonDiffLine {
  type: 'common';
  lines: { from: number; to: number };
  content: string;
}

const addedRegex = /^\+(.*)/;
export interface AddedDiffLine {
  type: 'added';
  lines: { to: number };
  content: string;
}

const removedRegex = /^-(.*)/;
export interface RemovedDiffLine {
  type: 'removed';
  lines: { from: number };
  content: string;
}

export type DiffLine =
  | HeaderDiffLine
  | CommonDiffLine
  | AddedDiffLine
  | RemovedDiffLine;

export interface DiffCounts {
  headers: number;
  additions: number;
  removals: number;
  common: number;
}

export interface DiffDocument {
  lines: DiffLine[];
  counts: DiffCounts;
}

export function parseDiff(diff: string): DiffDocument {
  const splitLines = diff.split('\n');
  const lines: DiffLine[] = [];

  const counts = { headers: 0, additions: 0, removals: 0, common: 0 };

  let from = { line: 1, count: 0, remaining: 0 };
  let to = { line: 1, count: 0, remaining: 0 };

  for (const line of splitLines) {
    if (line.startsWith('--- ') || line.startsWith('+++ ')) {
      continue;
    }

    let match: RegExpMatchArray | null;

    if ((match = line.match(headerRegex))) {
      const fromLine = parseInt(match[1]);
      const fromLineCount = parseInt(match[2]);
      const toLine = parseInt(match[3]);
      const toLineCount = parseInt(match[4]);

      lines.push({
        type: 'header',
        from: { line: fromLine, count: fromLineCount },
        to: { line: toLine, count: toLineCount },
      });

      from = {
        line: fromLine,
        count: fromLineCount,
        remaining: fromLineCount,
      };
      to = {
        line: toLine,
        count: toLineCount,
        remaining: toLineCount,
      };
      counts.headers++;
    }

    if ((match = line.match(commonRegex))) {
      lines.push({
        content: match[1],
        type: 'common',
        lines: {
          from: from.line + (from.count - from.remaining),
          to: to.line + (to.count - to.remaining),
        },
      });

      from.remaining--;
      to.remaining--;
      counts.common++;
    }

    if ((match = line.match(addedRegex))) {
      lines.push({
        content: match[1],
        type: 'added',
        lines: { to: to.line + (to.count - to.remaining) },
      });

      to.remaining--;
      counts.additions++;
    }

    if ((match = line.match(removedRegex))) {
      lines.push({
        content: match[1],
        type: 'removed',
        lines: { from: from.line + (from.count - from.remaining) },
      });

      from.remaining--;
      counts.removals++;
    }
  }

  return { lines, counts };
}

export function sliceDiff(
  diff: DiffDocument,
  selection: { side: 'from' | 'to'; start: number; end: number }
) {
  let startIndex = -1;
  let endIndex = -1;

  const counts = {
    headers: 0,
    additions: 0,
    removals: 0,
    common: 0,
  };

  for (let i = 0; i < diff.lines.length; i++) {
    const line = diff.lines[i];

    if (selection.side === 'from' && line.type === 'removed') {
      const lineNumber = line.lines.from;
      if (lineNumber === selection.start) {
        startIndex = i;
      }
      if (lineNumber === selection.end) {
        endIndex = i;
      }
    }

    if (selection.side === 'to' && line.type === 'added') {
      const lineNumber = line.lines.to;
      if (lineNumber === selection.start) {
        startIndex = i;
      }
      if (lineNumber === selection.end) {
        endIndex = i;
      }
    }

    if (line.type === 'common') {
      const lineNumber = line.lines[selection.side];
      if (lineNumber === selection.start) {
        startIndex = i;
      }
      if (lineNumber === selection.end) {
        endIndex = i;
      }
    }

    if (line.type === 'header') counts.headers++;
    if (line.type === 'common') counts.common++;
    if (line.type === 'added') counts.additions++;
    if (line.type === 'removed') counts.removals++;
  }

  if (startIndex === -1 || endIndex === -1) {
    return null;
  }

  return {
    lines: diff.lines.slice(startIndex, endIndex + 1),
    counts,
  };
}
